import React from 'react';
import { ArrowRight, Box, Play, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { GifShowcase } from './GifShowcase';

interface HeroSectionProps {
  user: any;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ user }) => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 30, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section className="relative min-h-[85vh] flex flex-col items-center justify-center overflow-hidden px-4 md:px-6 pt-32 md:pt-48 bg-transparent">
      {/* Structural Background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(rgba(0,0,0,0.03)_1px,transparent_1px)] [background-size:30px_30px] md:[background-size:40px_40px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-30" />
      </div>

      <motion.div 
        className="relative z-10 max-w-7xl mx-auto flex flex-col items-center text-center w-full"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.h1 
          className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-display text-zinc-950 mb-6 md:mb-8 leading-[1.1] md:leading-[1] tracking-tighter uppercase px-2"
          variants={itemVariants}
        >
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 via-cyan-400 to-cyan-600 animate-gradient-text drop-shadow-[0_10px_30px_rgba(6,182,212,0.1)]">Create Viral ASMR</span> <br />
          <span className="text-zinc-400">Physics & Ball Simulations</span>
        </motion.h1>

        <motion.p 
          className="text-sm md:text-base text-zinc-500 mb-10 max-w-2xl mx-auto leading-relaxed font-normal font-sans"
          variants={itemVariants}
        >
          Fun Circle Games is a creator platform for making satisfying ball simulations, ASMR physics videos, and viral faceless short-form content for TikTok and YouTube Shorts.
        </motion.p>

        <motion.div 
          className="flex flex-col sm:flex-row items-center gap-4 mb-12"
          variants={itemVariants}
        >
          {user ? (
            <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/studio" className={cn(buttonVariants(), "rounded-2xl px-10 h-14 text-[10px] font-bold uppercase tracking-[0.3em] bg-cyan-600 text-white hover:bg-cyan-700 shadow-xl shadow-cyan-600/10 transition-all font-sans border border-cyan-500/50")}>
                    <Play className="mr-3 h-3.5 w-3.5 fill-current text-white" />
                    Enter Studio
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/dashboard" className={cn(buttonVariants({ variant: "outline" }), "rounded-2xl px-10 h-14 text-[10px] font-bold uppercase tracking-[0.3em] bg-white text-cyan-600 border-cyan-100 hover:bg-cyan-50 transition-all font-sans")}>
                    Dashboard
                  </Link>
                </motion.div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/register" className={cn(buttonVariants(), "rounded-2xl px-10 h-14 text-[10px] font-bold uppercase tracking-[0.3em] bg-cyan-600 text-white hover:bg-cyan-700 shadow-xl shadow-cyan-600/10 transition-all font-sans border border-cyan-500/50")}>
                    Sign Up Free
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/login" className={cn(buttonVariants({ variant: "outline" }), "rounded-2xl px-10 h-14 text-[10px] font-bold uppercase tracking-[0.3em] border-cyan-100 bg-white hover:bg-cyan-50 text-cyan-600 transition-all font-sans")}>
                    Log In
                  </Link>
                </motion.div>
            </div>
          )}
        </motion.div>

        <motion.div 
          className="w-full"
          variants={itemVariants}
        >
          <GifShowcase />
        </motion.div>
      </motion.div>
    </section>
  );
};

